import { Injectable } from '@angular/core';
import {
    AllPublicationFolderE,
    FolderPublicationFilterI,
    PublicationFolderI,
    PublicationI,
    UserFileI
} from 'src/app/models/publication';
import { FolderContentsI, FolderFilterI, FolderI, FolderNameAndNotesI, PinnedFolderI } from 'src/app/models/folder';
import { FolderShareAccessI, FolderShareRequestI, FolderShareUserI } from 'src/app/models/folder-share';
import { FolderActivityI } from 'src/app/models/folder-activity';
import { BulkAddFavoritesResultI } from 'src/app/models/favorites';
import { ProjectFileI } from 'src/app/models/project';
import { map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import {
    CACHE_KEY_FOLDER_SHARE_USERS,
    FolderRepository,
    FolderShareUserRepository,
    HeaderMenuInformationRepository,
    skipWhileFolderShareUsersCached
} from 'src/app/store';
import { PublicationStatusE } from 'src/app/models/document';
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders } from '@angular/common/http';
import { AuthenticateService } from 'src/app/services/authenticate.service';
import { HttpPaginationResponseI } from 'src/app/models/http';
import { Pageable } from 'src/app/mixins/pagination/pagination.mixin';
import { MixpanelService } from 'src/app/services/mixpanel.service';
import { MessageBus } from 'src/app/messaging/MessageBus';
import { Configuration } from 'src/app/constant/configuration';

export interface PdfDownloadTypeI {
    file: Blob;
    filename: string;
}

export const EVENT_FOLDER_LIST_UPDATED = 'event:folderChannel-folderListUpdated';

@Injectable({ providedIn: 'root' })
export class FolderService {
    private serviceUrl = 'webfront/folders';

    constructor(
        public httpClient: HttpClient,
        private headerMenuInformationRepository: HeaderMenuInformationRepository,
        private folderRepository: FolderRepository,
        private folderShareUserRepository: FolderShareUserRepository,
        private authenticateService: AuthenticateService,
        private mixpanelService: MixpanelService,
        private messageBus: MessageBus,
        private configuration: Configuration
    ) {
        messageBus.channel(configuration.messageBusChannels.FOLDER_CHANNEL).subscribe(payload => {
            if (
                payload.message === configuration.events.folder.folderListUpdated ||
                payload.message === configuration.events.folder.folderUpdated
            ) {
                this.folderRepository.clearCacheOfUser(this.currentUserUuid);
            }
        });
    }

    get currentUserUuid(): string {
        return this.authenticateService.currentUser.uuid;
    }

    get currentCompanyUuid(): string {
        return this.authenticateService.currentUser.companyUuid;
    }

    updateFolder(folder: FolderI): Observable<unknown> {
        return this.httpClient.put(this.serviceUrl, folder).pipe(
            tap(() => {
                this.messageBus.channel(this.configuration.messageBusChannels.FOLDER_CHANNEL).next({
                    message: this.configuration.events.folder.folderListUpdated
                });
            })
        );
    }

    /**
     * Download a PDF that contains all the selected documents
     */
    downloadPDFForPublicationFolderList(folderPublicationList: PublicationFolderI[]): Observable<PdfDownloadTypeI> {
        return this.httpClient
            .post(this.serviceUrl + '/pdf/publications', folderPublicationList, {
                responseType: 'blob'
            })
            .pipe(
                map(data => {
                    const foldersName = [...new Set(folderPublicationList.map(f => f.folderName))] as string[];
                    const filename =
                        foldersName.length === 0 || foldersName.length > 1 ? 'my-documents' : foldersName[0];
                    return { file: data, filename: `${filename}.pdf` };
                })
            );
    }

    fetchFolders(filter: FolderFilterI, pageable: Pageable): Observable<HttpPaginationResponseI<FolderI[]>> {
        return this.httpClient.get<HttpPaginationResponseI<FolderI[]>>(this.serviceUrl, {
            params: {
                ...filter,
                page: pageable.page,
                size: pageable.size,
                ...(pageable.sort ? { sort: pageable.sort } : {})
            },
            headers: new HttpHeaders({ skipSpinner: 'true' })
        });
    }

    /**
     * @deprecated
     * Delete the folder publications from the list, prefer use `deleteFolderPublicationsById`
     */
    deleteFolderPublications(folderPublicationList: PublicationFolderI[]): Observable<void> {
        return this.httpClient.post<void>(this.serviceUrl + '/delete', folderPublicationList).pipe(
            tap(() => {
                this.folderRepository.clearCacheOfUser(this.currentUserUuid);
            })
        );
    }

    /**
     * remove publication from folder
     */
    deleteFolderPublicationById(
        folderId: number,
        folderPublicationId: number,
        folderPublicationType: AllPublicationFolderE
    ): Observable<void> {
        return this.httpClient
            .delete<void>(
                `${this.serviceUrl}/${folderId}/publications/${folderPublicationId}/delete/${folderPublicationType}`
            )
            .pipe(
                tap(() => {
                    this.folderRepository.clearCacheOfUser(this.currentUserUuid);
                })
            );
    }

    deleteFolderByIds(folderList: FolderI[]): Observable<void> {
        const folderIdList = folderList.map(folder => folder.id);
        return this.httpClient.post<void>(this.serviceUrl + '/deleteAll', folderIdList);
    }

    updateDocumentVersionFromFolderDocumentId(folderDocumentId: number): Observable<PublicationFolderI> {
        return this.httpClient
            .post<PublicationFolderI>(this.serviceUrl + '/user/myalert/updateDocumentVersion', folderDocumentId)
            .pipe(
                tap(() => {
                    this.folderRepository.clearCacheOfUser(this.currentUserUuid);
                })
            );
    }

    addFolderPublicationsToMyAlerts(folderPublicationList: PublicationFolderI[]): Observable<void> {
        return this.httpClient.post<void>(this.serviceUrl + '/addAlertToFolderDocuments', folderPublicationList).pipe(
            tap(() => {
                const numberAlertToAdd = folderPublicationList.filter(
                    folderPublication =>
                        folderPublication.userStatus !== PublicationStatusE.UP_TO_DATE &&
                        folderPublication.publication &&
                        folderPublication.publication.typeCode !== 'BOOK'
                ).length;
                this.headerMenuInformationRepository.incrementInformationNumberAlertsBy(numberAlertToAdd);
            })
        );
    }

    fetchFolder(folderId: number): Observable<FolderI> {
        return this.httpClient.get<FolderI>(`${this.serviceUrl}/${folderId}`);
    }

    fetchPinnedFolders(): Observable<PinnedFolderI[]> {
        return this.httpClient.get<PinnedFolderI[]>(`${this.serviceUrl}/pinned`, {
            headers: new HttpHeaders({ skipSpinner: 'true' })
        });
    }

    /**
     * The folder's full publicationList, with each document/bookpart's Document/BookPart resolved onto
     * `publication`. Left out of fetchFolder, which only sends each publication's identity, so a folder with
     * many publications can render before every one of them is resolved; callers overwrite their copy of
     * folder.publicationList with the result wholesale once it arrives.
     */
    fetchFolderPublications(folderId: number): Observable<Array<PublicationFolderI | UserFileI>> {
        return this.httpClient.get<Array<PublicationFolderI | UserFileI>>(
            `${this.serviceUrl}/${folderId}/publications`,
            { headers: new HttpHeaders({ skipSpinner: 'true' }) }
        );
    }

    /**
     * Same publicationList as fetchFolderPublications, plus the folder's bookReferences, so a folder already
     * displayed also picks up books saved in their entirety since it was loaded, in a single request.
     */
    fetchFolderContents(folderId: number): Observable<FolderContentsI> {
        return this.httpClient.get<FolderContentsI>(`${this.serviceUrl}/${folderId}/contents`, {
            headers: new HttpHeaders({ skipSpinner: 'true' })
        });
    }

    fetchFolderProjectPublications(folderId: number): Observable<ProjectFileI[]> {
        return this.httpClient.get<ProjectFileI[]>(`${this.serviceUrl}/${folderId}/project-publications`, {
            headers: new HttpHeaders({ skipSpinner: 'true' })
        });
    }

    createFolder(folderName: string, folderNotes: string): Observable<FolderI> {
        const payload: FolderNameAndNotesI = { name: folderName, notes: folderNotes };
        return this.httpClient.post<FolderI>(this.serviceUrl + '/createFolder', payload).pipe(
            tap(() => {
                this.folderRepository.clearCacheOfUser(this.currentUserUuid);
            })
        );
    }

    createFolderInProject(folderName: string, projectId: number): Observable<FolderI> {
        return this.httpClient.post<FolderI>(this.serviceUrl, { name: folderName, projectId }).pipe(
            tap(() => {
                this.folderRepository.clearCacheOfUser(this.currentUserUuid);
            })
        );
    }

    moveFolderToProject(folderId: number, projectId: number | null): Observable<FolderI> {
        return this.httpClient.put<FolderI>(`webfront/projects/folders/${folderId}/move`, { projectId }).pipe(
            tap(() => {
                this.folderRepository.clearCacheOfUser(this.currentUserUuid);
            })
        );
    }

    deleteFolder(folderId: number): Observable<void> {
        return this.httpClient.delete<void>(this.serviceUrl + '/' + folderId).pipe(
            tap(() => {
                this.messageBus.channel(this.configuration.messageBusChannels.FOLDER_CHANNEL).next({
                    message: this.configuration.events.folder.folderListUpdated
                });
            })
        );
    }

    getAvailableFolderPublicationList(
        filter: FolderPublicationFilterI,
        pagination: Pageable
    ): Observable<HttpPaginationResponseI<PublicationI[]>> {
        return this.httpClient.get<HttpPaginationResponseI<PublicationI[]>>(this.serviceUrl + '/publications/list', {
            params: {
                ...filter,
                sort: pagination.sort,
                isAscendingSort: true,
                pageNumber: pagination.page,
                pageSize: pagination.size
            },
            headers: new HttpHeaders({ skipSpinner: 'true' })
        });
    }

    copyFolder(folderId: number, newFolderName: string, newFolderNotes: string): Observable<FolderI> {
        const payload: FolderNameAndNotesI = { name: newFolderName, notes: newFolderNotes };
        return this.httpClient.post<FolderI>(this.serviceUrl + '/' + folderId + '/copy', payload).pipe(
            tap(() => {
                this.messageBus.channel(this.configuration.messageBusChannels.FOLDER_CHANNEL).next({
                    message: this.configuration.events.folder.folderListUpdated
                });
            })
        );
    }

    addPublicationToFolder(
        folderId: number,
        publicationId: number,
        publicationType: 'BOOK' | 'DOCUMENT'
    ): Observable<PublicationFolderI> {
        return this.httpClient
            .post<PublicationFolderI>(this.serviceUrl + '/' + folderId + '/publications', {
                publicationId,
                publicationType
            })
            .pipe(
                tap(() => {
                    this.folderRepository.clearCacheOfUser(this.currentUserUuid);
                })
            );
    }

    addFavoritesFolderToFolder(folderId: number, favoritesFolderId: number): Observable<BulkAddFavoritesResultI> {
        return this.httpClient
            .post<BulkAddFavoritesResultI>(this.serviceUrl + '/' + folderId + '/favorites', { favoritesFolderId })
            .pipe(
                tap(() => {
                    this.folderRepository.clearCacheOfUser(this.currentUserUuid);
                })
            );
    }

    uploadPrivateFileToFolder(
        folderId: number,
        payload: { file: File; title: string; consentDataProcessingAgreements: boolean }
    ): Observable<HttpEvent<unknown>> {
        const formData = new FormData();
        formData.append('file', payload.file);
        formData.append('fileName', payload.title);
        formData.append('consentDataProcessingAgreements', payload.consentDataProcessingAgreements ? '1' : '0');

        return this.httpClient
            .post(this.serviceUrl + `/${folderId}/upload`, formData, {
                reportProgress: true,
                observe: 'events',
                headers: new HttpHeaders({ skipSpinner: 'true', skipTechnicalAndBusinessError: 'true' })
            })
            .pipe(
                tap(event => {
                    if (event.type === HttpEventType.Response) {
                        this.mixpanelService.track(this.mixpanelService.constants.actions.uploadPrivateFile, {});
                    }
                })
            );
    }

    fetchShareableUsers(): Observable<FolderShareUserI[]> {
        return this.httpClient
            .get<FolderShareUserI[]>(`${this.serviceUrl}/shareable-users`, {
                headers: new HttpHeaders({ skipSpinner: 'true' })
            })
            .pipe(
                tap(users => this.folderShareUserRepository.setUsers(this.currentCompanyUuid, users)),
                skipWhileFolderShareUsersCached(`${CACHE_KEY_FOLDER_SHARE_USERS}-${this.currentCompanyUuid}`, {
                    returnSource: this.folderShareUserRepository.selectUsersByCompanyUuid$(this.currentCompanyUuid)
                })
            );
    }

    shareFolder(folderId: number, payload: FolderShareRequestI): Observable<void> {
        return this.httpClient.post<void>(`${this.serviceUrl}/${folderId}/share`, payload);
    }

    getFolderShares(folderId: number): Observable<FolderShareAccessI[]> {
        return this.httpClient.get<FolderShareAccessI[]>(`${this.serviceUrl}/${folderId}/shares`, {
            headers: new HttpHeaders({ skipSpinner: 'true' })
        });
    }

    setFolderPinned(folderId: number, pinned: boolean): Observable<void> {
        return this.httpClient.put<void>(`${this.serviceUrl}/${folderId}/pin`, pinned);
    }

    getFolderActivities(folderId: number, pageable: Pageable): Observable<HttpPaginationResponseI<FolderActivityI[]>> {
        return this.httpClient.get<HttpPaginationResponseI<FolderActivityI[]>>(
            `${this.serviceUrl}/${folderId}/activities`,
            {
                params: {
                    page: pageable.page,
                    size: pageable.size
                },
                headers: new HttpHeaders({ skipSpinner: 'true' })
            }
        );
    }
}
